import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ErrorMessageComponent } from "@/Common/Components/Errors/ErrorMessageComponent";
import { SubscriptionGroupMyleDto } from "@/Common/Models/Portal/MedfarPortal";
import { PaymentInterval, SubscriptionStatus } from "@/Common/Models/Portal/MedfarPortalEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { IPortalManagementPopupErrorFunction } from "./LinkMylePatientToPortalAccountDialog";
import { PortalActionConfirmationGenericPopup } from "./PortalActionConfirmationGenericPopup";
import { SubscriptionCreation } from "./SubscriptionCreation";
import { SubscriptionChangeAction, SubscriptionChangePlan } from "./SubscriptionDetails_ChangePlanPopup";

interface ISubscriptionDetailsProps {
    subscription: SubscriptionGroupMyleDto;
    patientContactId: string;
    areControlsDisabled?: boolean;
}

class SubscriptionDetailsState {
    public isLoading: boolean = false;
    public errors: string[];

    public isConfirmAccountSuspensionPopupVisible: boolean = false;
    public isConfirmAccountRestorePopupVisible: boolean = false;

    public changePlanPopupAction: SubscriptionChangeAction;
}

class SubscriptionDetails extends React.Component<ISubscriptionDetailsProps, SubscriptionDetailsState> {
    constructor(props: ISubscriptionDetailsProps) {
        super(props);
        this.state = new SubscriptionDetailsState();
    }

    public render(): JSX.Element {
        const subscription = this.props.subscription;
        const hasSubscription = subscription != null;

        const currentPlan = hasSubscription ? this.props.subscription.CurrentGroupPlan : null;
        const subscriptionPlan = hasSubscription ? cacheStore.GetPortalSubscriptionPlanInfo(currentPlan.SubscriptionPlanId) : null;
        const isAccountSuspended = hasSubscription ? subscription.Status === SubscriptionStatus.Suspended : false;

        let cssClass = "psingle-column centered";
        if (this.props.areControlsDisabled)
            cssClass = "fullWidth";

        return (
            <div className="block separated">

                {this.state.changePlanPopupAction && (
                    <SubscriptionChangePlan
                        patientContactId={this.props.patientContactId}
                        subscription={this.props.subscription}
                        onCloseCallBack={this.onClosePopups.bind(this)}
                        currentPlanId={this.props.subscription.CurrentGroupPlan.SubscriptionPlanId}
                        changeAction={this.state.changePlanPopupAction}
                    />
                )}

                {this.state.isConfirmAccountSuspensionPopupVisible && (
                    <PortalActionConfirmationGenericPopup
                        confirmationCallback={this.onConfirmSuspension.bind(this)}
                        confirmationTitle={cacheStore.GetString("ConfirmSubscriptionSuspensionLabel")}
                        confirmationMessage={cacheStore.GetString("SubscriptionSuspensionMessageLabel")}
                        onCloseCallback={this.onClosePopups.bind(this)}
                    />
                )}

                {this.state.isConfirmAccountRestorePopupVisible && (
                    <PortalActionConfirmationGenericPopup
                        confirmationCallback={this.onConfirmRestore.bind(this)}
                        confirmationTitle={cacheStore.GetString("ConfirmSubscriptionReactivationLabel")}
                        confirmationMessage={cacheStore.GetString("SubscriptioReactivationMessageLabel")}
                        onCloseCallback={this.onClosePopups.bind(this)}
                    />
                )}

                <div className="flex-container heading">
                    <h3 className="thin-font">{cacheStore.GetString("SubscriptionLabel")}: {cacheStore.GetString("DetailsLabel")} </h3>
                    {!this.props.areControlsDisabled && hasSubscription && (
                        <div>
                            <button onClick={this.displayCancelPlanPopup.bind(this)} className="btn btn-warning push-right-5">
                                <span>{cacheStore.GetString("CancelSubscriptionPlanLabel")}</span>
                            </button>
                            {!isAccountSuspended && (
                                <button onClick={this.displaySuspendAccountDialog.bind(this)} className="btn btn-primary push-right-5">
                                    <span>{cacheStore.GetString("Suspend2Label")}</span>
                                </button>
                            )}
                            {isAccountSuspended && (
                                <button onClick={this.displayRestoreAccountDialog.bind(this)} className="btn btn-primary push-right-5">
                                    <span>{cacheStore.GetString("RestoreLabel")}</span>
                                </button>
                            )}
                            <button onClick={this.displayChangePlanPopup.bind(this)} className="btn btn-primary push-right-5">
                                <span>{cacheStore.GetString("ModifyLabel")}</span>
                            </button>
                            <button onClick={this.displayResetPlanPopup.bind(this)} className="btn btn-primary">
                                <span>{cacheStore.GetString("ResetLabel")}</span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="flex-container center">
                    <div className={cssClass}>

                        <ErrorMessageComponent errors={this.state.errors} />

                        {!hasSubscription &&
                            <div style={{ paddingBottom: "10px" }}> {cacheStore.GetString("NoSubscriptionLabel")}</div>
                        }

                        {!hasSubscription && !this.props.areControlsDisabled && (
                            <SubscriptionCreation
                                patientContactId={this.props.patientContactId}
                                subscriptionPlans={cacheStore.portalConfiguration?.SubscriptionPlans}
                            />
                        )}

                        {!this.props.areControlsDisabled && hasSubscription && (
                            <table className="table">
                                <colgroup>
                                    <col className="c1" />
                                    <col className="c2" />
                                    <col className="c3" />
                                    <col className="c4" />
                                    <col />
                                    <col />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="center">{cacheStore.GetString("SubscriptionTypeLabel")}</th>
                                        <th className="center">{cacheStore.GetString("StartDateLabel")}</th>
                                        <th className="center">{cacheStore.GetString("RenewalDateLabel")}</th>
                                        <th className="center">{cacheStore.GetString("PaymentLabel")}</th>
                                        <th className="center">{cacheStore.GetString("StatusLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{subscriptionPlan.Name}</td>

                                        <td>{moment.utc(subscription.StartDate).format("YYYY-MM-DD")}</td>
                                        <td>
                                            {subscription.UpcomingPayment &&
                                                <span>{subscription.UpcomingPayment.Description}</span>
                                            }
                                            {!subscription.UpcomingPayment &&
                                                <span>-</span>
                                            }
                                        </td>
                                        <td>
                                            {subscription.UpcomingPayment && subscription.UpcomingPayment.SubscriptionGroupPlanPaymentInterval === PaymentInterval.Annually &&
                                                <span>{cacheStore.GetString("YearlyMasculineLabel")} - {subscription.UpcomingPayment.Amount.Total}$</span>
                                            }
                                            {subscription.UpcomingPayment && subscription.UpcomingPayment.SubscriptionGroupPlanPaymentInterval === PaymentInterval.Monthly &&
                                                <span>{cacheStore.GetString("MonthlyMasculineLabel")} - {subscription.UpcomingPayment.Amount.Total}$</span>
                                            }
                                            {subscription.UpcomingPayment && subscription.UpcomingPayment.SubscriptionGroupPlanPaymentInterval === PaymentInterval.Once &&
                                                <span>{cacheStore.GetString("LifetimeLabel")} - {subscription.UpcomingPayment.Amount.Total}$</span>
                                            }
                                            {subscription.UpcomingPayment && subscription.UpcomingPayment.SubscriptionGroupPlanPaymentInterval === PaymentInterval.Custom &&
                                                <span>{cacheStore.GetString("OtherLabel")} - {subscription.UpcomingPayment.Amount.Total}$</span>
                                            }
                                            {!subscription.UpcomingPayment &&
                                                <span>-</span>
                                            }
                                        </td>
                                        <td>
                                            {subscription.Status === SubscriptionStatus.Active &&
                                                <span>{cacheStore.GetString("ActiveLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Cancelled &&
                                                <span>{cacheStore.GetString("CancelledLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Unpaid &&
                                                <span>{cacheStore.GetString("UnpaidLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Unknown &&
                                                <span>{cacheStore.GetString("UnknownLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Suspended &&
                                                <span className="red-text">{cacheStore.GetString("SuspendedLabel")}</span>
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                        {this.props.areControlsDisabled && hasSubscription && (
                            <table className="table">
                                <colgroup>
                                    <col className="c1" />
                                    <col className="c2" />
                                    <col className="c3" />
                                    <col className="c4" />
                                    <col />
                                    <col />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="center">{cacheStore.GetString("SubscriptionTypeLabel")}</th>
                                        <th className="center">{cacheStore.GetString("StartDateLabel")}</th>
                                        <th className="center">{cacheStore.GetString("RenewalDateLabel")}</th>
                                        <th className="center">{cacheStore.GetString("PaymentLabel")}</th>
                                        <th className="center">{cacheStore.GetString("StatusLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{subscriptionPlan.Name}</td>

                                        <td>{moment.utc(subscription.StartDate).format("YYYY-MM-DD")}</td>
                                        <td>{subscription.UpcomingPayment ? moment.utc(subscription.UpcomingPayment.BillDate).format("YYYY-MM-DD") : "-"}</td>
                                        <td>
                                            {currentPlan.PaymentInterval === PaymentInterval.Annually &&
                                                <span>{cacheStore.GetString("YearlyMasculineLabel")} - {currentPlan.Rate.Total}$</span>
                                            }
                                            {currentPlan.PaymentInterval === PaymentInterval.Monthly &&
                                                <span>{cacheStore.GetString("MonthlyMasculineLabel")} - {currentPlan.Rate.Total}$</span>
                                            }
                                            {currentPlan.PaymentInterval === PaymentInterval.Once &&
                                                <span>{cacheStore.GetString("LifetimeLabel")} - {currentPlan.Rate.Total}$</span>
                                            }
                                            {currentPlan.PaymentInterval === PaymentInterval.Custom &&
                                                <span>{cacheStore.GetString("OtherLabel")} - {currentPlan.Rate.Total}$</span>
                                            }
                                        </td>
                                        <td>
                                            {subscription.Status === SubscriptionStatus.Active &&
                                                <span>{cacheStore.GetString("ActiveLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Cancelled &&
                                                <span>{cacheStore.GetString("CancelledLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Unpaid &&
                                                <span>{cacheStore.GetString("UnpaidLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Unknown &&
                                                <span>{cacheStore.GetString("UnknownLabel")}</span>
                                            }
                                            {subscription.Status === SubscriptionStatus.Suspended &&
                                                <span className="red-text">{cacheStore.GetString("SuspendedLabel")}</span>
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    public displaySuspendAccountDialog(): void {
        const s = new SubscriptionDetailsState();
        s.isConfirmAccountSuspensionPopupVisible = true;
        s.changePlanPopupAction = null;
        this.setState(s);
    }

    public displayRestoreAccountDialog(): void {
        const s = new SubscriptionDetailsState();
        s.isConfirmAccountRestorePopupVisible = true;
        s.changePlanPopupAction = null;
        this.setState(s);
    }

    public displayChangePlanPopup(): void {
        const s = new SubscriptionDetailsState();
        s.isConfirmAccountSuspensionPopupVisible = false;
        s.changePlanPopupAction = SubscriptionChangeAction.Change;
        this.setState(s);
    }

    public displayCancelPlanPopup(): void {
        const s = new SubscriptionDetailsState();
        s.isConfirmAccountSuspensionPopupVisible = false;
        s.changePlanPopupAction = SubscriptionChangeAction.Cancel;
        this.setState(s);
    }

    public displayResetPlanPopup(): void {
        const s = new SubscriptionDetailsState();
        s.isConfirmAccountSuspensionPopupVisible = false;
        s.changePlanPopupAction = SubscriptionChangeAction.Reset;
        this.setState(s);
    }

    public onClosePopups(): void {
        const s = new SubscriptionDetailsState();
        s.isConfirmAccountSuspensionPopupVisible = false;
        s.isConfirmAccountRestorePopupVisible = false;
        s.changePlanPopupAction = null;

        this.setState(s);
    }

    private onConfirmSuspension(onError: IPortalManagementPopupErrorFunction): void {
        const client = new Client();
        client.Post(ServerOperations.Server.Portal_Proxy_SuspendSubscription, { SubscriptionId: this.props.subscription.SubscriptionGroupId }, response => this.onConfirmSuspensionSuccess(response), err => onError(err));
    }

    private onConfirmRestore(onError: IPortalManagementPopupErrorFunction): void {
        const client = new Client();
        client.Post(ServerOperations.Server.Portal_Proxy_UnsuspendSubscription, { SubscriptionId: this.props.subscription.SubscriptionGroupId }, response => this.onConfirmSuspensionSuccess(response), err => onError(err));
    }

    private onConfirmSuspensionSuccess(response: ServerEvent): void {
        const errors = response.Errors as ErrorMessage[];
        if (errors.length !== 0)
            return;

        // todo reload portal patient account section
        const op = new LocalEvent(LocalOperations.Portal.Subscription_Load);
        op.AddItem("patientContactId", this.props.patientContactId);
        op.AddItem("subscriptionId", this.props.subscription.SubscriptionGroupId);
        LocalEventDispatcher.dispatch(op);
        this.onClosePopups();
    }
}

export { SubscriptionDetails };
